<script lang="ts" setup>
import { ref, computed } from "vue";
import { getTaxCalculations, submitTsd, getSubmissions, getSubmissionDetail, pollStatus, downloadXbrl, validateTsd, recalculateTaxes as recalculateTaxesApi } from "./tsdApi";
import { TsdSubmissionStatusLabels, TsdSubmissionLineStatusLabels } from "./types";
import type { TaxCalculationLine, TsdSubmissionRequestDto, SubmitTsdRequest, TsdValidationErrorDto } from "./types";

defineOptions({
  name: "TsdAdmin",
});

interface LogEntry {
  id: number;
  timestamp: Date;
  action: string;
  submissionId?: string;
  response: string;
  isError: boolean;
}

const companyId = ref<number | null>(null);

const now = new Date();
const periodYear = ref(now.getFullYear());
const periodMonth = ref(now.getMonth() + 1);

const payoutMonth6 = computed(() => {
  const m = String(periodMonth.value).padStart(2, "0");
  return `${periodYear.value}${m}`;
});

const monthNames = [
  "Jaanuar", "Veebruar", "Märts", "Aprill", "Mai", "Juuni",
  "Juuli", "August", "September", "Oktoober", "November", "Detsember",
];

function prevMonth() {
  if (periodMonth.value === 1) {
    periodMonth.value = 12;
    periodYear.value--;
  } else {
    periodMonth.value--;
  }
}

function nextMonth() {
  if (periodMonth.value === 12) {
    periodMonth.value = 1;
    periodYear.value++;
  } else {
    periodMonth.value++;
  }
}

const comment = ref("");

const lines = ref<TaxCalculationLine[]>([]);
const selectedIds = ref<Set<string>>(new Set());
const submissions = ref<TsdSubmissionRequestDto[]>([]);

const isLoadingLines = ref(false);
const isSubmitting = ref(false);
const isLoadingSubmissions = ref(false);
const error = ref<string | null>(null);

// Request/response log
const logEntries = ref<LogEntry[]>([]);
let logCounter = 0;
const showLogModal = ref(false);

// Poll loading state per submission
const pollingIds = ref<Set<string>>(new Set());

// Map taxCalculationId → { submissionId, status } for line-level cancel
const lineToSubmission = ref<Map<string, { submissionId: string; status: number }>>(new Map());

// Track which tax calculation line IDs have been submitted (client-side)
const submittedLineIds = ref<Set<string>>(new Set());

// Track line-level status after poll (taxCalcId → status number: 0=Pending, 1=Valid, 2=Error)
const lineStatuses = ref<Map<string, number>>(new Map());

// Track tax calculation IDs that have a pending cancel submission (T line not yet completed)
const pendingCancelIds = ref<Set<string>>(new Set());

const downloadXbrlChecked = ref(true);

// Validation
const validationErrors = ref<TsdValidationErrorDto[]>([]);
const isValidating = ref(false);
const showValidationPanel = ref(false);

// Display maps for submission ID and external ref per tax calc line
const lineSubmissionId = ref<Map<string, string>>(new Map());
const lineExternalRefId = ref<Map<string, string>>(new Map());

function isNewLine(lineId: string): boolean {
  return !submittedLineIds.value.has(lineId);
}

function isCancelCandidate(lineId: string): boolean {
  if (pendingCancelIds.value.has(lineId)) return false;
  const info = lineToSubmission.value.get(lineId);
  return !!info && (info.status === 30 || info.status === 40);
}

function isLineSelectable(lineId: string): boolean {
  if (cancelLockedIds.value.has(lineId)) return false;
  return isNewLine(lineId) || isCancelCandidate(lineId);
}

function lineAction(lineId: string): "R" | "T" | null {
  if (isNewLine(lineId)) return "R";
  if (isCancelCandidate(lineId)) return "T";
  return null;
}

const visibleLines = computed(() =>
    lines.value.filter((l) => !cancelledLineIds.value.has(l.id))
);

const selectableLines = computed(() =>
    visibleLines.value.filter((l) => isLineSelectable(l.id))
);

const allSelected = computed(() =>
    selectableLines.value.length > 0 && selectedIds.value.size === selectableLines.value.length
);

const selectedCount = computed(() => selectedIds.value.size);

const selectedRegisterIds = computed(() =>
    Array.from(selectedIds.value).filter((id) => lineAction(id) === "R")
);
const selectedCancelIds = computed(() =>
    Array.from(selectedIds.value).filter((id) => lineAction(id) === "T")
);

const isRecalculating = ref(false);

async function recalculateTaxes() {
  if (!companyId.value || !payoutMonth6.value) {
    error.value = "Company ID and Payout Month are required.";
    return;
  }
  isRecalculating.value = true;
  try {
    await recalculateTaxesApi(companyId.value, payoutMonth6.value);
    addLogEntry("Recalculate Taxes", { status: "ok" }, false);
  } catch (e) {
    addLogEntry("Recalculate Taxes", { error: (e as Error).message }, true);
  } finally {
    isRecalculating.value = false;
  }
  await loadLines();
}

async function handleValidate() {
  if (!companyId.value || !payoutMonth6.value) {
    error.value = "Company ID and Payout Month are required.";
    return;
  }
  if (selectedIds.value.size === 0) {
    error.value = "Select at least one tax calculation line.";
    return;
  }

  error.value = null;
  isValidating.value = true;
  const request = buildSubmitRequest();

  try {
    const errors = await validateTsd(companyId.value, request);
    validationErrors.value = errors;
    showValidationPanel.value = true;
    addLogEntry("Validate", { errorCount: errors.length, errors: errors.slice(0, 5) }, errors.length > 0);
  } catch (e) {
    const msg = (e as Error).message;
    addLogEntry("Validate", { error: msg }, true);
    error.value = msg;
  } finally {
    isValidating.value = false;
  }
}

function addLogEntry(action: string, response: unknown, isError: boolean, submissionId?: string) {
  logEntries.value.unshift({
    id: ++logCounter,
    timestamp: new Date(),
    action,
    submissionId,
    response: JSON.stringify(response, null, 2),
    isError,
  });
}

function toggleAll() {
  if (allSelected.value) {
    selectedIds.value = new Set();
  } else {
    selectedIds.value = new Set(selectableLines.value.map((l) => l.id));
  }
}

function toggleLine(id: string) {
  if (!isLineSelectable(id)) return;
  const next = new Set(selectedIds.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  selectedIds.value = next;
}

function clearSelection() {
  selectedIds.value = new Set();
}

async function loadLines() {
  if (!companyId.value || !payoutMonth6.value) {
    error.value = "Company ID and Payout Month are required.";
    return;
  }

  error.value = null;
  isLoadingLines.value = true;
  selectedIds.value = new Set();

  try {
    lines.value = await getTaxCalculations(companyId.value, payoutMonth6.value);
    if (lines.value.length === 0) {
      error.value = "No tax calculations found for this period.";
    }
  } catch (e) {
    error.value = (e as Error).message;
    lines.value = [];
  } finally {
    isLoadingLines.value = false;
  }

  await loadSubmissions();
}

interface LineTrackingState {
  submitted: Set<string>;
  statuses: Map<string, number>;
  lineToSub: Map<string, { submissionId: string; status: number }>;
  pendingCancel: Set<string>;
  cancelledIds: Set<string>;
}

// Lines that have been successfully cancelled — hidden from the table
const cancelledLineIds = ref<Set<string>>(new Set());

// Client-side cancel lock — survives loadSubmissions rebuilds
const cancelLockedIds = ref<Set<string>>(new Set());

function inferLineStatus(lineStatus: number, submissionStatus: number): number {
  if (lineStatus !== 0) return lineStatus;
  const isCompleted = submissionStatus === 30 || submissionStatus === 40 || submissionStatus === 50;
  if (!isCompleted) return lineStatus;
  return submissionStatus === 30 ? 100 : 200;
}

function buildLineTrackingState(
    subs: TsdSubmissionRequestDto[],
    details: PromiseSettledResult<Awaited<ReturnType<typeof getSubmissionDetail>>>[]
): LineTrackingState {
  const state: LineTrackingState = {
    submitted: new Set(),
    statuses: new Map(),
    lineToSub: new Map(),
    pendingCancel: new Set(),
    cancelledIds: new Set(),
  };

  for (let i = 0; i < subs.length; i++) {
    const sub = subs[i];
    const result = details[i];
    if (result.status !== "fulfilled") continue;

    for (const line of result.value.lines) {
      if (line.operationType === "T") {
        if (sub.status < 30) {
          state.pendingCancel.add(line.taxCalculationId);
        } else if (sub.status === 30) {
          state.cancelledIds.add(line.taxCalculationId);
        }
        continue;
      }

      state.submitted.add(line.taxCalculationId);
      state.statuses.set(line.taxCalculationId, inferLineStatus(line.status, sub.status));
      state.lineToSub.set(line.taxCalculationId, { submissionId: sub.id, status: sub.status });
    }
  }

  return state;
}

async function loadSubmissions() {
  if (!companyId.value || !payoutMonth6.value) return;

  isLoadingSubmissions.value = true;
  try {
    submissions.value = await getSubmissions(companyId.value, payoutMonth6.value);

    const details = await Promise.allSettled(
        submissions.value.map((sub) => getSubmissionDetail(companyId.value!, sub.id))
    );

    const state = buildLineTrackingState(submissions.value, details);
    submittedLineIds.value = state.submitted;
    lineStatuses.value = state.statuses;
    lineToSubmission.value = state.lineToSub;
    pendingCancelIds.value = state.pendingCancel;
    cancelledLineIds.value = state.cancelledIds;
    // Clear client-side lock for lines that are now confirmed cancelled
    for (const id of state.cancelledIds) {
      cancelLockedIds.value.delete(id);
    }
  } catch {
    submissions.value = [];
  } finally {
    isLoadingSubmissions.value = false;
  }
}

function buildSubmitRequest(): SubmitTsdRequest {
  return {
    payoutMonth6: payoutMonth6.value,
    taxCalculationIds: selectedRegisterIds.value,
    comment: comment.value,
    cancelTaxCalculationIds: selectedCancelIds.value.length > 0 ? selectedCancelIds.value : undefined,
  };
}

async function handleSubmit() {
  if (!companyId.value || !payoutMonth6.value) {
    error.value = "Company ID and Payout Month are required.";
    return;
  }

  if (selectedIds.value.size === 0) {
    error.value = "Select at least one tax calculation line.";
    return;
  }

  error.value = null;
  isSubmitting.value = true;
  const request = buildSubmitRequest();
  const registerIds = [...selectedRegisterIds.value];
  const cancelIds = [...selectedCancelIds.value];
  const allAffectedIds = [...registerIds, ...cancelIds];

  try {
    // Download XBRL alongside submit if checkbox is checked
    if (downloadXbrlChecked.value) {
      try {
        await downloadXbrl(companyId.value, request);
        addLogEntry("Download XBRL", { lines: selectedIds.value.size }, false);
      } catch (e) {
        addLogEntry("Download XBRL", { error: (e as Error).message }, true);
      }
    }

    const result = await submitTsd(companyId.value, request);

    // Immediately show submission ID on affected lines
    const nextSubIds = new Map(lineSubmissionId.value);
    for (const id of registerIds) {
      submittedLineIds.value.add(id);
      nextSubIds.set(id, result.submissionRequestId);
    }
    // Lock cancel lines — stays disabled until removed
    for (const id of cancelIds) {
      cancelLockedIds.value.add(id);
      pendingCancelIds.value.add(id);
      nextSubIds.set(id, result.submissionRequestId);
    }
    lineSubmissionId.value = nextSubIds;
    selectedIds.value = new Set();

    addLogEntry("Submit TSD", result, false, result.submissionRequestId);
    await loadSubmissions();

    // 3s → first poll (returns not-ready), show awaiting on all affected lines
    setTimeout(async () => {
      try {
        const poll1 = await pollStatus(companyId.value!, result.submissionRequestId);
        addLogEntry("Poll Status (1)", poll1, false, result.submissionRequestId);
      } catch (e) {
        addLogEntry("Poll Status (1)", { error: (e as Error).message }, true, result.submissionRequestId);
      }
      const nextExtIds = new Map(lineExternalRefId.value);
      const nextStatuses = new Map(lineStatuses.value);
      for (const id of allAffectedIds) {
        nextExtIds.set(id, result.externalReferenceId);
        nextStatuses.set(id, 10);
      }
      lineExternalRefId.value = nextExtIds;
      lineStatuses.value = nextStatuses;

      // 5s later → second poll (returns ready), backend persists Accepted
      setTimeout(async () => {
        try {
          const poll2 = await pollStatus(companyId.value!, result.submissionRequestId);
          addLogEntry("Poll Status (2)", poll2, false, result.submissionRequestId);
        } catch (e) {
          addLogEntry("Poll Status (2)", { error: (e as Error).message }, true, result.submissionRequestId);
        }
        await loadLines();
      }, 5000);
    }, 3000);
  } catch (e) {
    const msg = (e as Error).message;
    addLogEntry("Submit TSD", { error: msg }, true);
    error.value = msg;
  } finally {
    isSubmitting.value = false;
  }
}

function formatAmount(amount: number): string {
  if (amount === 0) return "-";
  const formatted = Math.abs(amount).toFixed(2).replace(".", ",");
  const parts = formatted.split(",");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const result = parts.join(",");
  return amount < 0 ? `-${result}` : result;
}

function formatGrossAmount(amount: number): string {
  const formatted = Math.abs(amount).toFixed(2).replace(".", ",");
  const parts = formatted.split(",");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const result = parts.join(",");
  return amount < 0 ? `-${result}` : result;
}

function formatPaymentDate(date: string | null): string {
  if (!date) return "";
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function statusLabel(status: number): string {
  return TsdSubmissionStatusLabels[status] ?? `Unknown (${status})`;
}

function formatDate(date: string | null): string {
  if (!date) return "—";
  return new Date(date).toLocaleString();
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString();
}
</script>

<template>
  <div class="tsd-admin">
    <h1>TSD Submission Test</h1>

    <div class="input-row">
      <div class="input-group">
        <label for="companyId">Company ID</label>
        <input
            id="companyId"
            v-model.number="companyId"
            type="number"
            placeholder="e.g. 123"
        />
      </div>
      <div class="period-selector">
        <button class="period-arrow" @click="prevMonth">&larr;</button>
        <div class="period-display">
          <span class="period-year">{{ periodYear }}</span>
          <span class="period-month">{{ monthNames[periodMonth - 1] }}</span>
        </div>
        <button class="period-arrow" @click="nextMonth">&rarr;</button>
      </div>
      <div class="input-group">
        <label for="comment">Comment</label>
        <input
            id="comment"
            v-model="comment"
            type="text"
            placeholder="Optional"
        />
      </div>
      <label class="xbrl-checkbox">
        <input type="checkbox" v-model="downloadXbrlChecked" />
        Save XBRL file
      </label>
      <button class="btn btn-recalc" :disabled="isRecalculating" @click="recalculateTaxes">
        {{ isRecalculating ? "Calculating..." : "Recalculate" }}
      </button>
      <button class="btn btn-load" :disabled="isLoadingLines" @click="loadLines">
        {{ isLoadingLines ? "Loading..." : "Load Lines" }}
      </button>
      <button
          v-if="logEntries.length > 0"
          class="btn btn-log"
          @click="showLogModal = true"
      >
        View Log ({{ logEntries.length }})
      </button>
    </div>

    <div v-if="error" class="error-msg">{{ error }}</div>

    <!-- Validation results panel -->
    <div v-if="showValidationPanel" class="validation-panel">
      <div class="validation-header">
        <h2>
          Validation Results
          <span v-if="validationErrors.length === 0" class="validation-ok">All checks passed</span>
          <span v-else class="validation-count">{{ validationErrors.length }} issue(s)</span>
        </h2>
        <button class="btn-close" @click="showValidationPanel = false">&times;</button>
      </div>
      <table v-if="validationErrors.length > 0" class="validation-table">
        <thead>
        <tr>
          <th>Severity</th>
          <th>Code</th>
          <th>Line</th>
          <th>Message</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(err, idx) in validationErrors" :key="idx" :class="{ 'row-warning': err.severity === 'Warning' }">
          <td>
            <span v-if="err.severity === 'Error'" class="severity-error">ERROR</span>
            <span v-else class="severity-warning">WARN</span>
          </td>
          <td class="mono">{{ err.code }}</td>
          <td class="mono">{{ err.lineNumber ?? '—' }}</td>
          <td>{{ err.message }}</td>
        </tr>
        </tbody>
      </table>
    </div>

    <div v-if="lines.length > 0" class="lines-section">
      <div class="lines-header">
        <h2>Tax Calculation Lines ({{ visibleLines.length }})</h2>
        <span class="selected-count">
          {{ selectedCount }} selected
          <template v-if="selectedRegisterIds.length > 0 && selectedCancelIds.length > 0">
            ({{ selectedRegisterIds.length }}R + {{ selectedCancelIds.length }}T)
          </template>
        </span>
      </div>

      <table class="lines-table">
        <thead>
        <tr>
          <th class="col-action">
            <input type="checkbox" :checked="allSelected" @change="toggleAll" />
          </th>
          <th class="col-recipient">Saaja</th>
          <th class="col-type-date">Väljamakse</th>
          <th class="col-amount">VM</th>
          <th class="col-amount">SM</th>
          <th class="col-amount">KP</th>
          <th class="col-amount">TK</th>
          <th class="col-amount">TTK</th>
          <th class="col-amount">MVT</th>
          <th class="col-amount">KTM</th>
          <th class="col-tracking">Tracking</th>
          <th class="col-status">Staatus</th>
        </tr>
        </thead>
        <tbody>
        <tr
            v-for="line in visibleLines"
            :key="line.id"
            :class="{
              selected: selectedIds.has(line.id) && lineAction(line.id) === 'R',
              'selected-cancel': selectedIds.has(line.id) && lineAction(line.id) === 'T',
              'line-disabled': !isLineSelectable(line.id)
            }"
            @click="toggleLine(line.id)"
        >
          <td class="col-action">
            <label
                class="action-check"
                :class="{ 'action-check-disabled': !isLineSelectable(line.id) }"
                @click.stop
            >
              <input
                  type="checkbox"
                  :checked="selectedIds.has(line.id)"
                  :disabled="!isLineSelectable(line.id)"
                  @change="toggleLine(line.id)"
              />
              <span v-if="lineAction(line.id) === 'R'" class="action-label action-submit">Submit</span>
              <span v-else-if="lineAction(line.id) === 'T'" class="action-label action-cancel">Cancel</span>
              <span v-else class="action-label action-none">—</span>
            </label>
          </td>
          <td class="col-recipient">
            <div class="recipient-name">{{ line.employeeName ?? line.employeeId.substring(0, 8) + '...' }}</div>
            <div class="recipient-code">{{ line.personalCode ?? '' }}</div>
          </td>
          <td class="col-type-date">
            <div class="type-code">{{ line.payoutTypeCode }}{{ line.payoutTypeDescription ? ' - ' + line.payoutTypeDescription : '' }}</div>
            <div class="type-date">{{ formatPaymentDate(line.paymentDate) }}</div>
          </td>
          <td class="col-amount">{{ formatGrossAmount(line.grossAmount) }}</td>
          <td class="col-amount">{{ formatAmount(line.socialTaxSum) }}</td>
          <td class="col-amount">{{ formatAmount(line.fundedPensionPayment) }}</td>
          <td class="col-amount">{{ formatAmount(line.unemploymentInsurance) }}</td>
          <td class="col-amount">{{ formatAmount(line.employerUnempInsurance) }}</td>
          <td class="col-amount">{{ formatAmount(line.usedNonTaxableAmount) }}</td>
          <td class="col-amount">{{ formatGrossAmount(line.incomeTaxSum) }}</td>
          <td class="col-tracking mono">
            <template v-if="lineSubmissionId.get(line.id)">
              <div class="tracking-sub">{{ lineSubmissionId.get(line.id) }}</div>
              <div v-if="lineExternalRefId.get(line.id)" class="tracking-ext">{{ lineExternalRefId.get(line.id) }}</div>
            </template>
          </td>
          <td class="col-status">
            <span v-if="lineStatuses.get(line.id) === 100" class="status-icon status-valid" title="Valid">&#10003;</span>
            <span v-else-if="lineStatuses.get(line.id) === 10" class="status-icon status-awaiting" title="Awaiting feedback">&#8987;</span>
            <span v-else-if="lineStatuses.get(line.id) === 200" class="status-icon status-error" title="Error">&#10007;</span>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Floating submit bar -->
    <Transition name="slide-up">
      <div v-if="selectedCount > 0" class="submit-bar">
        <span class="submit-bar-info">
          {{ selectedCount }} line(s) selected
          <template v-if="selectedRegisterIds.length > 0 && selectedCancelIds.length > 0">
            ({{ selectedRegisterIds.length }}R + {{ selectedCancelIds.length }}T)
          </template>
        </span>
        <button class="btn btn-cancel" @click="clearSelection">Cancel</button>
        <button
            class="btn btn-submit"
            :disabled="isSubmitting"
            @click="handleSubmit"
        >
          {{ isSubmitting ? "Submitting..." : "Submit" }}
        </button>
      </div>
    </Transition>

    <!-- Log modal -->
    <Teleport to="body">
      <div v-if="showLogModal" class="log-overlay" @click.self="showLogModal = false">
        <div class="log-modal">
          <div class="log-modal-header">
            <h2>Request Log</h2>
            <button class="btn-close" @click="showLogModal = false">&times;</button>
          </div>
          <div class="log-modal-body">
            <table v-if="logEntries.length > 0" class="log-table">
              <thead>
              <tr>
                <th>Time</th>
                <th>Action</th>
                <th>Submission ID</th>
                <th>Response</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="entry in logEntries" :key="entry.id" :class="{ 'log-row-error': entry.isError }">
                <td class="log-col-time">{{ formatTime(entry.timestamp) }}</td>
                <td>{{ entry.action }}</td>
                <td class="mono">{{ entry.submissionId ?? '—' }}</td>
                <td><pre class="log-pre">{{ entry.response }}</pre></td>
              </tr>
              </tbody>
            </table>
            <p v-else class="log-empty">No log entries yet.</p>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.tsd-admin {
  max-width: 100%;
  padding-bottom: 70px;
}

h1 {
  font-size: 1.5em;
  margin-bottom: 1.2em;
}

h2 {
  font-size: 1.15em;
  margin: 0;
}

.input-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 16px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.input-group label {
  font-size: 0.8em;
  font-weight: 600;
  color: #555;
}

.input-group input {
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 0;
  font-size: 0.9em;
  width: 140px;
  height: 34px;
  box-sizing: border-box;
}

.input-group input[type="number"] {
  width: 100px;
}

.period-selector {
  display: flex;
  align-items: center;
  gap: 0;
  align-self: flex-end;
}

.period-arrow {
  background: #f5f7f9;
  border: 1px solid #ccc;
  padding: 0 12px;
  cursor: pointer;
  font-size: 1em;
  line-height: 1;
  transition: background 0.15s;
  align-self: stretch;
}

.period-arrow:hover {
  background: #e8ecf0;
}

.period-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2px 16px;
  border-top: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
  background: #fff;
  min-width: 100px;
  height: 34px;
  box-sizing: border-box;
  line-height: 1.2;
}

.period-year {
  font-size: 0.85em;
  font-weight: 600;
  color: #333;
}

.period-month {
  font-size: 0.75em;
  color: #666;
}

.xbrl-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85em;
  cursor: pointer;
  user-select: none;
  align-self: flex-end;
  height: 34px;
}

.btn {
  padding: 7px 14px;
  border: none;
  border-radius: 0;
  font-size: 0.9em;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  height: 34px;
  box-sizing: border-box;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-recalc {
  background: #f59e0b;
  color: #fff;
}

.btn-recalc:hover:not(:disabled) {
  background: #d97706;
}

.btn-load {
  background: #3b82f6;
  color: #fff;
}

.btn-load:hover:not(:disabled) {
  background: #2563eb;
}

.btn-log {
  background: #f5f7f9;
  border: 1px solid #ccc;
  color: #555;
}

.btn-log:hover {
  background: #e8ecf0;
}

.error-msg {
  color: #d32f2f;
  background: #ffebee;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 12px;
  font-size: 0.9em;
}

.btn-validate {
  background: #7c3aed;
  color: #fff;
}

.btn-validate:hover:not(:disabled) {
  background: #6d28d9;
}

.validation-panel {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 16px;
  overflow: hidden;
}

.validation-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f5f7f9;
  border-bottom: 1px solid #e0e0e0;
}

.validation-header h2 {
  display: flex;
  align-items: center;
  gap: 10px;
}

.validation-ok {
  font-size: 0.8em;
  font-weight: 500;
  color: #2e7d32;
  background: #e8f5e9;
  padding: 2px 8px;
  border-radius: 3px;
}

.validation-count {
  font-size: 0.8em;
  font-weight: 500;
  color: #c62828;
  background: #ffebee;
  padding: 2px 8px;
  border-radius: 3px;
}

.validation-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85em;
}

.validation-table th,
.validation-table td {
  padding: 6px 12px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
}

.validation-table th {
  background: #fafafa;
  font-weight: 600;
  color: #555;
  font-size: 0.85em;
}

.validation-table .row-warning {
  background: #fffde7;
}

.severity-error {
  display: inline-block;
  background: #c62828;
  color: #fff;
  font-size: 0.75em;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 2px;
}

.severity-warning {
  display: inline-block;
  background: #f59e0b;
  color: #fff;
  font-size: 0.75em;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 2px;
}

.lines-section {
  margin-top: 16px;
}

.lines-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.selected-count {
  font-size: 0.85em;
  color: #666;
}

.lines-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875em;
}

.lines-table th,
.lines-table td {
  padding: 6px 10px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.lines-table th {
  background: #f5f7f9;
  font-weight: 600;
  font-size: 0.85em;
  color: #555;
}

.lines-table tbody tr {
  cursor: pointer;
  transition: background 0.1s;
}

.lines-table tbody tr:hover {
  background: #f0f4ff;
}

.lines-table tbody tr.selected {
  background: #e8f5e9;
}

.lines-table tbody tr.selected-cancel {
  background: #fce4ec;
}

.col-action {
  width: 90px;
  text-align: center;
}

.action-check {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
}

.action-check-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.action-label {
  font-size: 0.8em;
  font-weight: 600;
}

.action-submit {
  color: #2e7d32;
}

.action-cancel {
  color: #c62828;
}

.action-none {
  color: #999;
}

.col-amount {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.col-recipient {
  min-width: 140px;
}

.recipient-name {
  font-weight: 500;
}

.recipient-code {
  font-size: 0.8em;
  color: #888;
}

.col-type-date {
  max-width: 200px;
}

.type-code {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.type-date {
  font-size: 0.8em;
  color: #888;
}

.col-tracking {
  font-size: 0.7em;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tracking-sub {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tracking-ext {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #888;
}

.col-status {
  white-space: nowrap;
  text-align: center;
  width: 50px;
}

.status-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.status-awaiting {
  width: 20px;
  height: 20px;
  color: #999;
  font-size: 14px;
}

.status-valid {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #2e7d32;
  color: #fff;
  font-size: 12px;
}

.status-error {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #c62828;
  color: #fff;
  font-size: 12px;
}

.pending-cancel {
  background: #fce4ec !important;
  cursor: not-allowed !important;
}

.pending-cancel:hover {
  background: #fce4ec !important;
}

.line-disabled {
  opacity: 0.45;
  cursor: not-allowed !important;
  background: #f5f5f5 !important;
}

.line-disabled:hover {
  background: #f5f5f5 !important;
}

.mono {
  font-family: "Consolas", "Monaco", monospace;
  font-size: 0.85em;
}

/* Floating submit card */
.submit-bar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  z-index: 100;
}

.submit-bar-info {
  font-size: 0.85em;
  color: #555;
  white-space: nowrap;
}

.btn-cancel {
  background: #f5f5f5;
  border: 1px solid #ccc;
  color: #555;
}

.btn-cancel:hover {
  background: #e0e0e0;
}

.btn-submit {
  background: #2eb336;
  color: #fff;
}

.btn-submit:hover:not(:disabled) {
  background: #259b2d;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.2s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}

/* Log modal */
.log-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.log-modal {
  background: #fff;
  border-radius: 6px;
  width: 90vw;
  max-width: 900px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.log-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.4em;
  cursor: pointer;
  color: #888;
  padding: 0 4px;
}

.btn-close:hover {
  color: #333;
}

.log-modal-body {
  overflow: auto;
  padding: 12px 16px;
}

.log-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85em;
}

.log-table th,
.log-table td {
  padding: 6px 10px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
  vertical-align: top;
}

.log-table th {
  background: #f5f7f9;
  font-weight: 600;
  color: #555;
  position: sticky;
  top: 0;
}

.log-row-error {
  background: #fff8f8;
}

.log-row-error td {
  color: #c62828;
}

.log-col-time {
  white-space: nowrap;
  color: #888;
}

.log-pre {
  margin: 0;
  font-size: 0.85em;
  max-height: 150px;
  overflow: auto;
  white-space: pre-wrap;
}

.log-empty {
  color: #888;
  text-align: center;
  padding: 24px;
}
</style>
